import { PACIFIC_BLUE_MARLIN, blueMarlinCounts, spawningYearOf, seasonDays } from '../sim/marlin';
import { BLUE_MARLIN_SUPPLY } from '../sim/data/marlin/pacific';
import { BLUE_MARLIN_NORTH_SHARE } from '../sim/data/marlin/warm';
import { MARLIN_TAGS } from './data/marlinTags';
import { epochDayOf } from '../sim/calendar';
import type { World } from '../sim/types';

/** What NOAA's billfish tags say of where the marlin tagged off Hawaii were caught again (`scripts/data/marlinTags.ts`). */
export interface MarlinTags {
  /** Blue marlin tagged and released in the Pacific, and the years they span (of those whose year is given). */
  released: number;
  firstYear: number;
  lastYear: number;
  /** Of those released off Hawaii and caught again, both places given: how many, how many caught south of the equator, north of 40°N, within 200 nautical miles of where they were tagged, past 1,000, and the farthest, km. */
  hawaiiRecaptured: number;
  southOfEquator: number;
  northOf40: number;
  within200Nmi: number;
  past1000Nmi: number;
  farthestKm: number;
}

const n = (x: number): string => Math.round(x).toLocaleString('en-US');
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

/**
 * The Pacific blue marlin on the map's Pacific sites (M108, `sim/marlin.ts`):
 * the whole stock today by sex, its young, its spawning females' weight and
 * what the fishing takes, each beside the assessment's; how many are in each
 * low band this month (`northShareOn`); and where the fish tagged off Hawaii
 * were caught again (NOAA's tags). Pure, tested in node
 * (`tests/worldMap.test.ts`).
 */
export function blueMarlinText(world: World): string {
  const life = PACIFIC_BLUE_MARLIN;
  const day = epochDayOf(world);
  const c = blueMarlinCounts(life, world.migrants.blueMarlin, BLUE_MARLIN_SUPPLY, day);
  const spawning = spawningYearOf(life, day).day < seasonDays(life);
  const t = MARLIN_TAGS;
  const share = (pick: (xs: number[]) => number): string => `${Math.round(pick([...BLUE_MARLIN_NORTH_SHARE]) * 100)}% of it in ${MONTHS[BLUE_MARLIN_NORTH_SHARE.indexOf(pick([...BLUE_MARLIN_NORTH_SHARE]))]}`;
  // The assessment's figures: the ISC 2026 assessment's Plenary Report, Table 7-1 (p. 40: SSB 47,244 t and catch
  // 17,738 t in 2024) and Table 7-2 (p. 44: SSB 42,038 t and catch 13,662 t a year, 2021-2023).
  return `${n(c.population)} in the whole Pacific, one stock (${n(c.females)} females, ${n(c.males)} males, ${n(c.young)} of them born ${spawning ? 'this season' : 'last season'}): `
    + `${n(c.north)} in the North Pacific's low band and ${n(c.south)} in the South Pacific's this month, spread over the water warmer than 24 °C, which moves north in the northern summer (the North Pacific's ${share((xs) => Math.min(...xs))}, ${share((xs) => Math.max(...xs))}). `
    + `Its spawning females ${n(c.spawnersT)} t at the season's start (the ISC's 2026 assessment: 42,038 t a year in 2021 to 2023, 47,244 t in 2024). `
    + `The stock spawns from May to September in the game, the western North Pacific's season${spawning ? ': now' : ''}. Fishing takes ${n(c.caughtAYear)} over the coming year, ${n(c.caughtTAYear)} t (the assessment's 13,662 t a year in 2021 to 2023, 17,738 t in 2024). `
    + `Of ${n(t.hawaiiRecaptured)} tagged off Hawaii by anglers and caught again (NOAA's billfish tagging, ${t.firstYear} to ${t.lastYear}), ${n(t.within200Nmi)} were within 200 nautical miles of where they were tagged, ${n(t.past1000Nmi)} more than 1,000 away, ${n(t.southOfEquator)} south of the equator and ${t.northOf40 === 0 ? 'none' : n(t.northOf40)} north of 40°N, the farthest ${n(t.farthestKm)} km`;
}
